import { _ } from '/@/locale';
import type { ClaimProblem, ClaimStatus, ClaimType } from '/@/types/claim';
import type { CropMonitoring } from '/@/types/crop-monitoring';

export const CLAIM_TYPES: ClaimType[] = ['SEEDS', 'CHEMICALS'];

/** Problems a claim can report, per claim type — mirrors `Claim.PROBLEMS_BY_TYPE` on the backend. */
export const PROBLEMS_BY_TYPE: Record<ClaimType, ClaimProblem[]> = {
    SEEDS: ['PRE_PLANTING', 'EMERGENCE', 'PERFORMANCE'],
    CHEMICALS: ['PRE_APPLICATION', 'PERFORMANCE'],
};

export const getProblemOptions = (type: ClaimType | ''): ClaimProblem[] => (type ? PROBLEMS_BY_TYPE[type] : []);

/** A claim can be registered once it has a type and a problem valid for that type. */
export const isClaimValid = (type: ClaimType | '', problem: ClaimProblem | ''): boolean =>
    problem !== '' && getProblemOptions(type).includes(problem);

/**
 * The claim's default date for `problem`, or null when there is none: Presiembra takes the
 * crop monitoring's planting date (planting is never stored as a stage), Emergencia the date
 * of its corn emergence stage.
 */
export const getProblemStageDate = (problem: ClaimProblem | '', cropMonitoring?: CropMonitoring): string | null => {
    if (problem === 'PRE_PLANTING') return cropMonitoring?.plantingDate || null;
    if (problem === 'EMERGENCE') {
        const stage = cropMonitoring?.cornCropMonitoring?.stages?.find((s) => s.phenology === 'emergence');
        return stage?.date ?? null;
    }
    return null;
};

export const claimTypeLabel = (type: ClaimType): string => (type === 'SEEDS' ? _('Semillas') : _('Químicos'));

export const claimTypeDescription = (type: ClaimType): string =>
    type === 'SEEDS'
        ? _('Fallas de nacimiento, germinación o performance')
        : _('Daños o fallas del producto, antes o después de aplicarlo');

export const claimProblemLabel = (problem: ClaimProblem): string =>
    ({
        PRE_PLANTING: _('Presiembra'),
        EMERGENCE: _('Emergencia'),
        PERFORMANCE: _('Performance'),
        PRE_APPLICATION: _('Preaplicación'),
    })[problem];

export const claimStatusLabel = (status: ClaimStatus): string => (status === 'OPEN' ? _('Abierto') : _('Cerrado'));
